import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ProductOptionUpdateInput } from "@/modules/options/domain/contracts";
import { isDevMode } from "@/shared/lib/config";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircleIcon } from "lucide-react";
import { useProductOptionQuery, useUpdateProductOption } from "../api/queries";
import { OptionsFormTour, useOptionsFormTour } from "../tour";
import { OptionForm } from "../ui/OptionForm";

export function OptionEditPage() {
  const nav = useNavigate({ from: "/app/catalog/options/$id/edit" });
  const { id } = useParams({ from: "/app/catalog/options/$id/edit" });
  const { data, ...result } = useProductOptionQuery(id);
  const updateOption = useUpdateProductOption(id);
  const { run, handleJoyrideCallback, startTour } = useOptionsFormTour();

  const option = data;
  const optionName =
    option && typeof option.name === "object" && option.name !== null
      ? ((option.name as { lo?: string })?.lo ??
        (option.name as { en?: string })?.en ??
        option.slug)
      : (option?.slug ?? id ?? "ແກ້ໄຂ");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຕົວເລືອກ", to: "/app/catalog/options" },
            { label: optionName },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂ Product Option{" "}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            </h2>
            <p className="text-muted-foreground">
              ປັບຊື່ option ແລະຄ່າທີ່ໃຊ້ສໍາລັບ variant.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/options" } as any)}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ Product Option"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!option ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <OptionForm
                mode="edit"
                option={option}
                onSubmit={async (values: ProductOptionUpdateInput) => {
                  try {
                    await updateOption.mutateAsync(values);
                    nav({ to: "/app/catalog/options" } as any);
                  } catch {
                    // Errors are handled by the mutation's onError
                  }
                }}
                submitting={updateOption.isPending}
                onDevFill={isDevMode ? () => {} : undefined}
              />
            </div>
          )}
        </QueryState>
        <OptionsFormTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
